import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/client'
import { Badge, getStatusBadge, Avatar, Loading } from '../components/ui'
import { MyWorkSEO } from '../hooks/useSEO'
import type { Job, Submission } from '../types'

interface MyWorkJob extends Job {
  application: {
    id: string
    accepted: boolean
    createdAt: string
  }
  latestSubmission: Submission | null
}

interface MyWorkResponse {
  jobs: MyWorkJob[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function MyWorkPage() {
  const [jobs, setJobs] = useState<MyWorkJob[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchWork()
  }, [])

  const fetchWork = async () => {
    try {
      const data = await api.get<MyWorkResponse>('/applications/my/work')
      setJobs(data.jobs)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load work')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <MyWorkSEO />
      <div className="page-header">
        <div>
          <h1>My Work</h1>
          <p className="text-secondary">Jobs you're working on or have completed</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {isLoading ? (
        <Loading message="Loading your work..." />
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h3>No active work</h3>
          <p>Apply for jobs to get started</p>
          <Link to="/jobs" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Browse Jobs
          </Link>
        </div>
      ) : (
        <div className="items-list">
          {jobs.map((job) => (
            <div key={job.id} className="card-static">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Badge status={getStatusBadge(job.status).variant}>
                      {getStatusBadge(job.status).label}
                    </Badge>
                    {job.latestSubmission && (
                      <Badge status={getStatusBadge(job.latestSubmission.status).variant}>
                        Submission: {getStatusBadge(job.latestSubmission.status).label}
                      </Badge>
                    )}
                  </div>
                  <Link to={`/jobs/${job.id}`}>
                    <h3 style={{ margin: 0 }}>{job.title}</h3>
                  </Link>
                </div>
                <span className="usdc-amount">${job.reward} USDC</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <Avatar name={job.poster.name} size="sm" />
                <span className="text-secondary">Posted by {job.poster.name}</span>
                {job.deadline && (
                  <span className="text-secondary">
                    Due {new Date(job.deadline).toLocaleDateString()}
                  </span>
                )}
              </div>

              {(job.status === 'IN_PROGRESS' || (job.latestSubmission?.status === 'REVISION_REQUESTED')) && (
                <Link to={`/my-work/submit/${job.id}`} className="btn btn-primary btn-sm">
                  {job.latestSubmission?.status === 'REVISION_REQUESTED' ? 'Submit Revision' : 'Submit Work'}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
